"use client";

import { ArrowLeft, Book, Fullscreen, Play, X } from "lucide-react";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { cn } from "@/lib/utils";
import parse, { Element, type DOMNode } from "html-react-parser";

// ── helpers ───────────────────────────────────────────────────────────────────

export const resolveIframeSrc = (src: string) => {
  if (process.env.NODE_ENV !== "development") return src;
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : "http://localhost:3000";
  try {
    const url = new URL(
      src.replace(/https?:\/\/www\.writeinstone\.com/, origin),
    );
    url.searchParams.set("dev", "true");
    return url.toString();
  } catch {
    return src;
  }
};

// Stable DOM id for each widget's inline spacer, used for scroll-to-anchor
export const getWidgetId = (src: string) =>
  `widget-${src
    .replace(/[^a-zA-Z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(-48)}`;

const findIframeNode = (node: DOMNode): Element | null => {
  if (!(node instanceof Element)) return null;
  if (node.name === "iframe") return node;
  for (const child of node.children) {
    if (child instanceof Element) {
      const found = findIframeNode(child);
      if (found) return found;
    }
  }
  return null;
};

// ── context ───────────────────────────────────────────────────────────────────

interface LightboxCtxValue {
  openSrc: string | null;
  pipSrc: string | null;
  articleContent: string;
  readingMode: boolean;
  setReadingMode: (v: boolean) => void;
  open: (src: string) => void;
  switchTo: (src: string) => void;
  close: () => void;
  enterPiP: (src: string) => void;
  exitPiP: () => void;
  hoverReadingPanel: boolean | null;
  setHoverReadingPanel: (hover: boolean) => void;
}

const LightboxCtx = createContext<LightboxCtxValue>({
  openSrc: null,
  pipSrc: null,
  articleContent: "",
  readingMode: false,
  setReadingMode: () => {},
  open: () => {},
  switchTo: () => {},
  close: () => {},
  enterPiP: () => {},
  exitPiP: () => {},
  hoverReadingPanel: false,
  setHoverReadingPanel: () => {},
});

export const useLightbox = () => useContext(LightboxCtx);

// ── reading panel widget previews ─────────────────────────────────────────────

const WidgetPlaceholder = ({
  isActive,
  onActivate,
}: {
  widgetSrc: string;
  isActive: boolean;
  onActivate: () => void;
}) => (
  <div className="relative my-2 w-full aspect-video rounded-lg overflow-hidden bg-stone-900 dark:bg-stone-800 flex items-center justify-center">
    {isActive ? (
      <div className="flex items-center gap-2">
        <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
        <span className="text-xs font-Cabin text-white/70">
          Playing in portal
        </span>
      </div>
    ) : (
      <button
        onClick={onActivate}
        className="flex flex-col items-center gap-2 group"
        aria-label="Switch to this widget"
      >
        <div className="bg-white/10 rounded-full p-3 group-hover:bg-white/20 transition-colors">
          <Play className="h-5 w-5 text-white fill-white" />
        </div>
        <span className="text-[11px] font-Cabin text-white/50 group-hover:text-white/80 transition-colors">
          Switch to this widget
        </span>
      </button>
    )}
  </div>
);

const parseForReading = (
  html: string,
  activeSrc: string,
  onActivateSrc: (src: string) => void,
) =>
  parse(html, {
    replace: (domNode: DOMNode) => {
      if (!(domNode instanceof Element)) return;

      const containsIframe = (node: DOMNode): boolean =>
        node instanceof Element &&
        node.children.some(
          (child) =>
            child instanceof Element &&
            (child.name === "iframe" || containsIframe(child)),
        );

      let rawSrc: string | null = null;

      if (
        (domNode.name === "div" &&
          domNode.attribs?.style?.includes("padding-bottom")) ||
        (domNode.name === "figure" &&
          !domNode.attribs?.class?.includes("youtube"))
      ) {
        if (containsIframe(domNode))
          rawSrc = findIframeNode(domNode)?.attribs?.src ?? null;
      }

      if (domNode.name === "iframe" && domNode.attribs?.src)
        rawSrc = domNode.attribs.src;

      if (rawSrc !== null) {
        const widgetSrc = resolveIframeSrc(rawSrc);
        return (
          <WidgetPlaceholder
            widgetSrc={widgetSrc}
            isActive={widgetSrc === activeSrc}
            onActivate={() => onActivateSrc(widgetSrc)}
          />
        );
      }
    },
  });

// ── top bar (first flex row of the lightbox) ──────────────────────────────────

export const LightboxChrome = () => {
  const {
    openSrc,
    articleContent,
    readingMode,
    setReadingMode,
    close,
    enterPiP,
  } = useLightbox();

  const handleReadMode = useCallback(() => {
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    if (isMobile && openSrc) {
      enterPiP(openSrc);
    } else {
      setReadingMode(!readingMode);
    }
  }, [openSrc, readingMode, setReadingMode, enterPiP]);

  if (!openSrc) return null;

  return (
    <div className="flex items-center justify-between px-4 py-3 shrink-0">
      <button
        onClick={close}
        className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-Cabin"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="hidden sm:inline">Back</span>
      </button>
      <div className="flex items-center gap-4">
        {articleContent && (
          <button
            onClick={handleReadMode}
            className="text-white/70 hover:text-white transition-colors"
            aria-label={readingMode ? "Full widget" : "Reading mode"}
          >
            {readingMode ? (
              <span className="group/theatre items-center gap-4 flex group">
                <span className="group-hover/theatre:opacity-100 transition-opacity opacity-0">
                  Theatre
                </span>
                <Fullscreen className="h-5 w-5" />
              </span>
            ) : (
              <span className=" items-center gap-4 flex group/read">
                <span className="group-hover/read:opacity-100 transition-opacity opacity-0">
                  Read
                </span>
                <Book className="h-5 w-5" />
              </span>
            )}
          </button>
        )}
        {/* <button
          onClick={close}
          className="text-white/70 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button> */}
      </div>
    </div>
  );
};

// ── reading panel (desktop flex sibling of the iframe) ────────────────────────

export const LightboxReadingPanel = () => {
  const {
    openSrc,
    articleContent,
    readingMode,
    switchTo,
    setHoverReadingPanel,
  } = useLightbox();

  const readingContent = useMemo(
    () =>
      openSrc && articleContent
        ? parseForReading(articleContent, openSrc, switchTo)
        : null,
    [articleContent, openSrc, switchTo],
  );

  return (
    <div
      className={cn(
        "hidden md:block shrink-0 overflow-y-auto bg-background transition-all duration-300",
        readingMode ? "w-2/5 px-8 py-6" : "w-0 overflow-hidden",
      )}
      onMouseEnter={() => setHoverReadingPanel(true)}
      onMouseLeave={() => setHoverReadingPanel(false)}
    >
      {readingMode && readingContent && (
        <div className="wp-content text-stone-800 dark:text-stone-300 max-w-prose">
          {readingContent}
        </div>
      )}
    </div>
  );
};

// ── scrollbar widget markers (desktop only) ───────────────────────────────────

const ScrollbarMarkers = () => {
  const [markers, setMarkers] = useState<
    { top: number; height: number; el: HTMLElement }[]
  >([]);
  const containerRef = useRef<HTMLElement | null>(null);

  const { openSrc } = useLightbox();

  const compute = useCallback(() => {
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>("[data-stone-widget]"),
    );
    if (!nodes.length) return;

    // Find the inner scroll container (not window — AppContentFrame is fixed-height)
    if (!containerRef.current) {
      let cur: HTMLElement | null = nodes[0].parentElement;
      while (cur) {
        const { overflowY } = getComputedStyle(cur);
        if (overflowY === "auto" || overflowY === "scroll") {
          containerRef.current = cur;
          break;
        }
        cur = cur.parentElement;
      }
      if (!containerRef.current)
        containerRef.current = document.documentElement;
    }

    const container = containerRef.current;
    const totalHeight = container.scrollHeight;
    if (!totalHeight) return;
    const containerRect = container.getBoundingClientRect();

    setMarkers(
      nodes.map((el) => {
        const rect = el.getBoundingClientRect();
        // stable regardless of current scrollTop: scrollTop cancels with rect.top shift
        const absoluteTop = rect.top - containerRect.top + container.scrollTop;
        return {
          top: (absoluteTop / totalHeight) * 100,
          height: Math.max((rect.height / totalHeight) * 100, 0),
          el,
        };
      }),
    );
  }, []);

  useEffect(() => {
    let ro: ResizeObserver | null = null;
    const id = requestAnimationFrame(() => {
      compute();
      if (containerRef.current) {
        ro = new ResizeObserver(compute);
        ro.observe(containerRef.current);
      }
    });
    return () => {
      cancelAnimationFrame(id);
      ro?.disconnect();
    };
  }, [compute]);

  if (!markers.length) return null;

  const scrollbarWidth =
    typeof window !== "undefined"
      ? window.innerWidth - document.documentElement.clientWidth
      : 15;

  return (
    <div
      className={cn(
        "fixed top-0 bottom-0 w-2  pointer-events-none hidden md:block",
        !openSrc && "z-9999",
      )}
      style={{ right: scrollbarWidth + 4 }}
    >
      {markers.map(({ top, height, el }, i) => (
        <button
          key={i}
          onClick={() =>
            el.scrollIntoView({ behavior: "smooth", block: "center" })
          }
          className="absolute inset-x-0 rounded-sm bg-purple-500/80 hover:bg-purple-400 transition-colors pointer-events-auto cursor-pointer"
          style={{ top: `${top}%`, height: `${height}%`, minHeight: 6 }}
          aria-label="Scroll to Stone widget"
        />
      ))}
    </div>
  );
};

// ── provider ──────────────────────────────────────────────────────────────────

export const LightboxProvider = ({
  children,
  articleContent,
}: {
  children: ReactNode;
  articleContent: string;
}) => {
  const [openSrc, setOpenSrc] = useState<string | null>(null);
  const [pipSrc, setPipSrc] = useState<string | null>(null);
  const [readingModesBySrc, setReadingModesBySrc] = useState<
    Record<string, boolean>
  >({});

  const readingMode = openSrc ? readingModesBySrc[openSrc] ?? false : false;

  const setReadingMode = useCallback(
    (value: boolean) => {
      if (!openSrc) return;
      setReadingModesBySrc((prev) =>
        prev[openSrc] === value ? prev : { ...prev, [openSrc]: value },
      );
    },
    [openSrc],
  );

  const open = useCallback((src: string) => {
    setPipSrc(null);
    setOpenSrc(src);
  }, []);

  const close = useCallback(() => {
    setOpenSrc(null);
  }, []);

  const enterPiP = useCallback((src: string) => {
    setOpenSrc(null);
    setPipSrc(src);
    setTimeout(() => {
      document
        .getElementById(getWidgetId(src))
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
  }, []);

  const switchTo = useCallback(
    (src: string) => {
      setOpenSrc(src);
      if (readingMode) {
        setReadingModesBySrc((prev) =>
          prev[src] ? prev : { ...prev, [src]: true },
        );
      }
    },
    [readingMode],
  );

  const exitPiP = useCallback(() => setPipSrc(null), []);

  const [hoverReadingPanel, setHoverReadingPanel] = useState<boolean | null>(
    null,
  );

  return (
    <LightboxCtx.Provider
      value={{
        openSrc,
        pipSrc,
        articleContent,
        readingMode,
        setReadingMode,
        open,
        switchTo,
        close,
        enterPiP,
        exitPiP,
        hoverReadingPanel,
        setHoverReadingPanel,
      }}
    >
      {children}
      <ScrollbarMarkers />
    </LightboxCtx.Provider>
  );
};
